<template>
  <div class="import-step step-inner-padded d-flex justify-content-center" data-testid="oc-import-step">
    <div class="card card-wrapper" style="max-width: 800px; width: 100%">
      <div class="card-header d-block p-4">
        <h3>{{ $t('order_creation.import_step.processing_title') }}</h3>
        <p class="text-muted m-0">{{ $t('order_creation.import_step.processing_description') }}</p>
      </div>
      <div class="card-body p-4">
        <div class="row">
          <div class="col-6">
            <div class="browser-card card">
              <div class="browser-container">
                <div class="browser-container-top">
                  <span class="browser-container-dot"></span>
                  <span class="browser-container-dot"></span>
                  <span class="browser-container-dot"></span>
                  <span
                    v-if="!scanTimelineIsDone"
                    class="status-indicator status-blue status-indicator-animated position-absolute"
                    style="right: 2px; top: 0px">
                    <span class="status-indicator-circle"></span>
                    <span class="status-indicator-circle"></span>
                    <span class="status-indicator-circle"></span>
                  </span>
                  <span v-else>
                    <span class="status-indicator status-green position-absolute" style="right: 2px; top: 0px">
                      <span class="status-indicator-circle"></span>
                      <span class="status-indicator-circle"></span>
                      <span class="status-indicator-circle"></span>
                    </span>
                  </span>
                </div>
                <div class="browser-container-content">
                  <div class="scanning-container">
                    <div
                      class="scanning-container-inner"
                      :class="[scanningInProgress && previewImageLoaded ? 'active' : '']">
                      <div class="scanning-container-line"></div>
                    </div>
                  </div>
                  <div v-if="order.creation_source !== 'file_import'">
                    <div v-if="!previewImageLoaded" class="card placeholder-glow m-3">
                      <div class="ratio ratio-21x9 card-img-top placeholder"></div>
                      <div class="card-body">
                        <div class="placeholder col-9 mb-3"></div>
                        <div class="placeholder placeholder-xs col-10"></div>
                        <div class="placeholder placeholder-xs col-11"></div>
                        <div class="mt-3">
                          <a
                            href="#"
                            tabindex="-1"
                            class="btn btn-primary disabled placeholder col-4"
                            aria-hidden="true"></a>
                        </div>
                      </div>
                    </div>
                    <template v-else>
                      <img
                        :src="previewImageUrl"
                        class="img-fluid"
                        style="max-width: 100%; max-height: 100%; object-fit: contain" />
                    </template>
                  </div>
                  <div class="browser-container-file" v-else>
                    <IconFileSearch class="text-secondary" :size="84" />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="col-6">
            <div class="timeline-container">
              <WizardScanTimeline :timeline="scanTimeline" :is-done="scanTimelineIsDone" />
            </div>
          </div>
        </div>
      </div>
      <div class="card-footer bg-white" />
    </div>
  </div>
</template>
<script lang="ts" setup>
import { storeToRefs } from 'pinia';
import { onMounted, watch } from 'vue';
import WizardScanTimeline from '@/components/orders/wizard_sections/WizardScanTimeline.vue';
import { useImportOrderCreationStore } from '@/stores/importOrderCreation';
import { IconFileSearch } from '@tabler/icons-vue';
import { useOrderCreationStore } from '@/stores/orderCreation';
import { isAbortError } from '@/api/client';

const orderCreationStore = useOrderCreationStore();
const { order } = storeToRefs(orderCreationStore);
const importStore = useImportOrderCreationStore();
const {
  scanningInProgress,
  scanTimeline,
  scanTimelineIsDone,
  statusMessagesIsDone,
  previewImageLoaded,
  previewImageUrl,
} = storeToRefs(importStore);

const allowNext = defineModel<boolean>('allowNext');

// Initialize the analysis step when component mounts
onMounted(async () => {
  try {
    await importStore.handleExtraction();
  } catch (e: any) {
    if (isAbortError(e)) {
      return;
    }
  }
});

watch(
  () => statusMessagesIsDone.value,
  (newVal) => {
    if (newVal) allowNext.value = true;
  }
);
</script>
<style scoped>
.import-step .card-wrapper > .card-body {
  justify-content: center;
  background-color: #eff6fd;
}

.browser-container-dot {
  height: 12px;
  width: 12px;
  background-color: #bbb;
  border-radius: 50%;
  display: inline-block;
  margin-left: 5px;
}

.browser-card {
  background-color: #f1f1f1;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.15);
  border-radius: 10px;
  overflow: hidden;

  /* transform: perspective(1000px) rotateY(5deg); */
}

.browser-container-top {
  padding: 10px;
  background: #f1f1f1;
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
}

.browser-container-content {
  display: block;
  position: relative;
  width: 100%;
  height: 100%;
}

.browser-container-file {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.1);
  height: 250px;
  border-radius: 10px;
  margin: 10px;
}

.timeline-container {
  overflow-y: auto;
  overflow-x: hidden;
  background: #f1f1f1;
  height: 100%;
}

.scanning-container {
  display: block;
  z-index: 10;
  position: absolute;
  width: 100%;
  height: 100%;
  padding: 10px;
}

.scanning-container-inner {
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.2);
  border-radius: 10px;
  overflow: hidden;
  opacity: 0;
  transition: opacity 0.3s ease-in-out;
}

.scanning-container-inner.active {
  opacity: 1;
}

@keyframes scannline {
  0% {
    margin-top: 0px;
  }

  50% {
    margin-top: 80%;
  }

  100% {
    margin-top: 0%;
  }
}

.scanning-container-line {
  width: 100%;
  height: 3px;
  background-color: red;
  box-shadow: 0 0 30px rgba(255, 0, 0, 1);
  animation-name: scannline;
  animation-duration: 5s;
  animation-iteration-count: infinite;
}

.slide-fade-enter-active .card {
  transition: all 0.4s ease-out;
  position: absolute;
}

.slide-fade-leave-active .card {
  position: absolute;
  opacity: 0;
}

.slide-fade-enter-from .card,
.slide-fade-leave-to .card {
  transform: translateX(50px);
  opacity: 0;
}
</style>
